import React, { useMemo, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  FileText, 
  User,
  Menu,
  X,
  LayoutDashboard,
  ShoppingCart,
  Palette,
  LogOut,
  Bell,
  DollarSign,
  Globe,
  Briefcase,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import DesignCaseNotifications from './DesignCaseNotifications';

const SellerLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'FinanceBotSup_bot';
  const telegramDeepLink = useMemo(() => (botUsername ? `https://t.me/${botUsername}?start=link` : null), [botUsername]);

  const menuItems = useMemo(() => ([
    {
      name: 'Thống kê',
      icon: LayoutDashboard,
      path: '/seller'
    },
    {
      name: 'Đơn hàng',
      icon: ShoppingCart,
      path: '/seller/orders'
    },
    {
      name: 'Design',
      icon: Palette,
      path: '/seller/design-cases'
    },
    {
      name: 'Work Cases',
      icon: Briefcase,
      children: [
        { name: 'Thống kê', icon: LayoutDashboard, path: '/seller/work-cases/stats' },
        { name: 'Tôi đã giao', icon: Briefcase, path: '/seller/work-cases/created' },
        { name: 'Được giao cho tôi', icon: Briefcase, path: '/seller/work-cases/assigned' }
      ]
    },
    {
      name: 'Tài chính',
      icon: DollarSign,
      path: '/seller/finance'
    },
    {
      name: 'Tài nguyên',
      icon: FileText,
      path: '/seller/resources'
    },
    {
      name: 'Build Website',
      icon: Globe,
      path: '/seller/build-website'
    },
    {
      name: 'Thông báo',
      icon: Bell,
      path: '/seller/notifications',
      isNotification: true
    },
    {
      name: 'Thông tin cá nhân',
      icon: User,
      path: '/seller/profile'
    }
  ]), []);

  const isChildActive = (children = []) =>
    children.some((c) => location.pathname === c.path || location.pathname.startsWith(c.path));

  const toggleSubmenu = (name) =>
    setOpenMenus((prev) => ({ ...prev, [name]: !prev[name] }));

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };



  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile menu overlay */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}
        onClick={toggleMobileMenu}
      />
      
      {/* Sidebar */}
      <nav className={`fixed inset-y-0 left-0 w-64 bg-white shadow-lg z-40 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300 ease-in-out lg:static lg:shadow-none lg:bg-transparent`}>
        <div className="p-4 lg:p-6 h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex flex-col items-center justify-center w-full">
              <img 
                src="https://res.cloudinary.com/dypjamkif/image/upload/v1754457019/Dvt_Hulo_logo_prc1hv.png" 
                alt="DVT Hulo Logo" 
                className="h-12 w-[90%] object-contain"
              />
              <span className="text-xl font-semibold text-gray-700 mt-2">
                Seller
              </span>
            </div>
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>


          
          {/* Navigation */}
          <nav className="flex-1 space-y-2">
            {/* Telegram link warning if not linked */}
            {user && !user?.id_telegram && telegramDeepLink && (
              <div className="px-3 lg:px-4 py-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 flex items-start">
                <div className="mt-0.5 mr-3">⚠️</div>
                <div className="flex-1 text-sm">
                  Bạn chưa liên kết Telegram. 
                  <a href={telegramDeepLink} target="_blank" rel="noreferrer" className="ml-1 underline text-blue-600">
                    Liên kết ngay
                  </a>
                </div>
              </div>
            )}
            {menuItems.map((item) => {
              const Icon = item.icon;

              if (item.isNotification) {
                return (
                  <div key={item.name} className="relative">
                    <DesignCaseNotifications />
                  </div>
                );
              }

              if (item.children && item.children.length > 0) {
                const parentActive = isChildActive(item.children);
                const isOpen = openMenus[item.name] || parentActive;
                return (
                  <div key={item.name}>
                    <button
                      type="button"
                      onClick={() => toggleSubmenu(item.name)}
                      className={`w-full flex items-center justify-between px-3 lg:px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        parentActive ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-600' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <span className="flex items-center">
                        <Icon className={`w-4 h-4 lg:w-5 lg:h-5 mr-3 ${parentActive ? 'text-blue-600' : 'text-gray-500'}`} />
                        <span className="text-sm lg:text-base">{item.name}</span>
                      </span>
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    {isOpen && (
                      <div className="ml-6 mt-1 space-y-1 border-l border-gray-200 pl-3">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const activeChild = location.pathname === child.path;
                          return (
                            <Link
                              key={child.name}
                              to={child.path}
                              className={`flex items-center px-3 py-2 text-sm rounded-md ${
                                activeChild ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <ChildIcon className={`w-4 h-4 mr-3 ${activeChild ? 'text-blue-600' : 'text-gray-400'}`} />
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center px-3 lg:px-4 py-3 text-sm font-medium rounded-lg transition-colors group ${
                    isActive
                      ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Icon className={`w-4 h-4 lg:w-5 lg:h-5 mr-3 ${
                    isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                  }`} />
                  <span className="text-sm lg:text-base">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="mt-auto pt-6 border-t border-gray-200">
            {/* User Info */}
            <div className="flex items-center space-x-3 mb-4">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.name?.charAt(0).toUpperCase() || 'S'}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || 'Seller'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email || 'seller@example.com'}
                </p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={() => {
                sessionStorage.removeItem('token');
                window.location.href = '/';
              }}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Đăng xuất
            </button>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between p-4">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900"> Seller Management</h1>
            <div className="w-10"></div> {/* Spacer for centering */}
          </div>
        </div>

        {/* Content area */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>


    </div>
  );
};

export default SellerLayout; 
